import { useState, useEffect } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = [["active", "Active"], ["future", "Future"], ["inactive", "Inactive (history)"]];

function StatusPick({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1" data-testid="mz-f-status"><SelectValue /></SelectTrigger>
      <SelectContent>{STATUS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function MarketDialog({ market, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(market ? { ...market } : { status: "future", order: 500 }); }, [open, market]);
  const save = async () => {
    const body = { name: f.name, status: f.status, order: Number(f.order) || 0, notes: f.notes || "" };
    try { market ? await api.put(`/mz/markets/${market.id}`, body) : await api.post("/mz/markets", body); toast.success("Market saved"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="market-dialog">
        <DialogHeader><DialogTitle className="font-display">{market ? "Edit Market" : "Add Market"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Market / city name</Label><Input className="mt-1" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mz-f-name" />
            {market && <p className="mt-1 text-[11px] text-slate-400">Renaming keeps the same market ID and updates linked records.</p>}</div>
          <div><Label className="text-xs">Status</Label><StatusPick value={f.status} onChange={(v) => setF({ ...f, status: v })} /></div>
          <div><Label className="text-xs">Display order</Label><Input type="number" className="mt-1" value={f.order ?? ""} onChange={(e) => setF({ ...f, order: e.target.value })} data-testid="mz-f-order" /></div>
          <div className="col-span-2"><Label className="text-xs">Internal notes</Label><Textarea rows={2} className="mt-1" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="mz-f-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} data-testid="mz-market-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ZoneDialog({ zone, marketId, markets, reps, open, onOpenChange, onSaved }) {
  const [f, setF] = useState({});
  const [sp, setSp] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF(zone ? { ...zone } : { market_id: marketId, status: "active", capacity: 32, order: 0, boundary_status: "Draft — confirm" });
    setSp((zone?.salespeople || []).map((s) => s.id));
  }, [open, zone, marketId]);
  const save = async () => {
    const body = { name: f.name, market_id: f.market_id, status: f.status, order: Number(f.order) || 0, capacity: Number(f.capacity) || 0,
      boundary_description: f.boundary_description || "", boundary_status: f.boundary_status, notes: f.notes || "" };
    try {
      const { data } = zone ? await api.put(`/mz/zones/${zone.id}`, body) : await api.post("/mz/zones", body);
      await api.put(`/mz/zones/${data.id}/salespeople`, { user_ids: sp });
      toast.success("Zone saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="zone-dialog">
        <DialogHeader><DialogTitle className="font-display">{zone ? "Edit Zone" : "Add Zone"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Zone name</Label><Input className="mt-1" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="zf-name" /></div>
          <div><Label className="text-xs">Parent market</Label>
            <Select value={f.market_id} onValueChange={(v) => setF({ ...f, market_id: v })}><SelectTrigger className="mt-1" data-testid="zf-market"><SelectValue /></SelectTrigger>
              <SelectContent>{markets.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">Status</Label><StatusPick value={f.status} onChange={(v) => setF({ ...f, status: v })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Capacity</Label><Input type="number" className="mt-1" value={f.capacity ?? ""} onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="zf-capacity" /></div>
            <div><Label className="text-xs">Order</Label><Input type="number" className="mt-1" value={f.order ?? ""} onChange={(e) => setF({ ...f, order: e.target.value })} data-testid="zf-order" /></div>
          </div>
          <div className="col-span-2"><div className="flex items-center justify-between"><Label className="text-xs">Boundary description</Label>
            <Select value={f.boundary_status} onValueChange={(v) => setF({ ...f, boundary_status: v })}><SelectTrigger className="h-7 w-40 text-xs" data-testid="zf-boundary-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Draft — confirm">Draft — confirm</SelectItem><SelectItem value="Confirmed">Confirmed</SelectItem></SelectContent></Select></div>
            <Textarea rows={3} className="mt-1" value={f.boundary_description || ""} onChange={(e) => setF({ ...f, boundary_description: e.target.value })} data-testid="zf-boundary" /></div>
          <div className="col-span-2"><Label className="text-xs">Assigned salespeople</Label>
            <div className="mt-1 grid grid-cols-2 gap-1.5">{reps.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-sm"><Checkbox checked={sp.includes(r.id)} onCheckedChange={(v) => setSp(v ? [...sp, r.id] : sp.filter((x) => x !== r.id))} data-testid={`zf-rep-${r.id}`} />{r.name}</label>))}
              {!reps.length && <p className="text-xs text-slate-400">No sales representatives yet.</p>}</div></div>
          <div className="col-span-2"><Label className="text-xs">Internal notes</Label><Textarea rows={2} className="mt-1" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="zf-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={save} data-testid="zf-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
